import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Divider,
  Paper,
  Grid,
  Typography,
  TextField,
  Box,
  CircularProgress,
  useTheme,
  TablePagination,
  IconButton,
  Modal,
  Avatar,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoIcon from "@mui/icons-material/Info";

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { toast, ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import DescriptionIcon from "@mui/icons-material/Description";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonIcon from "@mui/icons-material/Person";
import Timeline from "@mui/icons-material/Timeline";
import Numbers from "@mui/icons-material/Numbers";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DownloadIcon from "@mui/icons-material/Download";
import CreateIcon from "@mui/icons-material/Create";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { styled } from "@mui/material/styles";

import {
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";

import api from "../../../utils/Url";
import moment from "moment";

import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import autoTable from "jspdf-autotable";

interface FileDocument {
  fileID: number;
  fileName: string;
  fileNo: string;
  fileType: number;
  fileGroup: number;
  fileCategory: number;
  fileClass: number;
  fileSubject: number;
  synopsis: string;
  remark: string;
  refID: number;
  totalPages: number;
  fromPage: number;
  toPage: number;
  status: string;
  originLocation: string;
  currentLocation: string;
  zoneID: number;
  wardID: number;
  createdDate: string;
  createdBy: string;
  updatedBy: string;
  updatedDate: string;
  rfid: string;
  senderID: string;
  receiverID: string;
  fileDoc: string; // This will contain the base64 encoded file
}

interface PDFPreviewModalProps {
  open: boolean;
  onClose: () => void;
  pdfBlob: Blob | null;
  onDownload: () => void;
}
// Custom pagination actions component
function TablePaginationActions(props: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.875rem",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const TransferHistoryModal = ({ open, onClose, transferData }: {
  open: boolean,
  onClose: () => void,
  transferData: any
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="transfer-history-modal"
      aria-describedby="transfer-history-details"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '60vw', xl: '50vw' },
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 3,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header with gradient background */}
        <Box sx={{
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          color: 'white',
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {t("text.AuditTrailDetail")}
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t("text.FileName")}: {transferData?.fileName || t("text.NA")} | {t("text.FileNumber")}: {transferData?.fileNumber || t("text.NA")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                transform: 'scale(1.1)'
              },
              transition: 'transform 0.2s'
            }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>

        {/* Main content with scrollable area */}
        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={3}>
            {/* Transfer Details Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                height: '100%',
                background: 'linear-gradient(to bottom right, #f5f5f5, #ffffff)'
              }}>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <SwapHorizIcon color="primary" /> {t("text.TransferDetails")}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <DetailItem
                  label={t("text.FileNumber")}
                  value={transferData?.fileNumber}
                  icon={<Numbers fontSize="small" />}
                />
                <DetailItem
                  label={t("text.Location")}
                  value={transferData?.fromLocation}
                  icon={<LocationOnIcon fontSize="small" />}
                />
                {/* <DetailItem
                  label={t("text.ToLocation")}
                  value={transferData?.toLocation || t("text.NotSpecified")}
                  icon={<LocationOnIcon fontSize="small" />}
                /> */}
                <DetailItem
                  label={t("text.Through")}
                  value={transferData?.throughBy || t("text.NA")}
                  icon={<AccountTreeIcon fontSize="small" />}
                />
              </Paper>
            </Grid>

            {/* Personnel Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                height: '100%',
                background: 'linear-gradient(to bottom right, #f5f5f5, #ffffff)'
              }}>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <PeopleAltIcon color="primary" /> {t("text.Personnel")}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <DetailItem
                  label={t("text.Sender")}
                  value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{
                        width: 24,
                        height: 24,
                        bgcolor: stringToColor(transferData?.senderName || '')
                      }}>
                        {transferData?.senderName?.charAt(0) || '?'}
                      </Avatar>
                      {transferData?.senderName || t("text.NA")}
                    </Box>
                  }
                />
                <DetailItem
                  label={t("text.Receiver")}
                  value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{
                        width: 24,
                        height: 24,
                        bgcolor: stringToColor(transferData?.receiverName || '')
                      }}>
                        {transferData?.receiverName?.charAt(0) || '?'}
                      </Avatar>
                      {transferData?.receiverName || t("text.NA")}
                    </Box>
                  }
                />
                <DetailItem
                  label={t("text.InitiatedBy")}
                  value={transferData?.createdBy || t("text.NA")}
                  icon={<PersonIcon fontSize="small" />}
                />
              </Paper>
            </Grid>

            {/* Timeline Section */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                background: 'linear-gradient(to bottom right, #f5f5f5, #ffffff)'
              }}>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Timeline color="primary" /> {t("text.Timeline")}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 3,
                  justifyContent: 'space-between'
                }}>
                  <TimelineItem
                    icon={<EventAvailableIcon color="primary" />}
                    title={t("text.FileDate")}
                    date={moment.utc(transferData?.fileDate).format("DD/MM/YYYY") || t("text.NA")}
                  />
                  <TimelineItem
                    icon={<CreateIcon color="primary" />}
                    title={t("text.CreatedDate")}
                    date={moment.utc(transferData?.createdDate).format("DD/MM/YYYY") || t("text.NA")}
                  />
                  <TimelineItem
                    icon={<DownloadIcon color="primary" />}
                    title={t("text.ReceivedDate")}
                    date={moment.utc(transferData?.receivedDate).format("DD/MM/YYYY") || t("text.NA")}
                  />
                  <TimelineItem
                    icon={<SendIcon color={transferData?.transferDate ? "primary" : "disabled"} />}
                    title={t("text.TransferDate")}
                    date={transferData?.transferDate ? moment.utc(transferData.transferDate).format("DD/MM/YYYY") : t("text.NA")}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Footer with action buttons */}

      </Box>
    </Modal>
  );
};

const FileTransferHistoryModal = ({
  open,
  onClose,
  transferData
}: {
  open: boolean,
  onClose: () => void,
  transferData: any
}) => {
  const { t } = useTranslation(); // Add translation hook
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const theme = useTheme();

  useEffect(() => {
    if (open && transferData?.fileID) {
      fetchTransferHistory();
    }
  }, [open, transferData]);

  const fetchTransferHistory = async () => {
    try {
      setLoading(true);
      const payload = {
        hid: -1,
        fileID: transferData.fileID,
        fileNumber: "",
        fromLocation: "",
        toLocation: "",
        refId: "",
        sender: "",
        receiver: "",
        user_Id: "",
      };

      const response = await api.post(`FileMovementHistory/GetAuditTrailReport`, payload);
      setHistoryData(response.data.data || []);
    } catch (error) {
      toast.error(t("text.FailedToFetchTransferHistory")); // Translated error message
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="file-transfer-history-modal"
      aria-describedby="file-transfer-history-details"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '60vw', xl: '50vw' },
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 3,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 50%, #80deea 100%)',
        border: '1px solid rgba(0, 150, 136, 0.3)'
      }}>
        {/* Header with gradient */}
        <Box sx={{
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          color: 'white',
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{
              fontWeight: 700,
              letterSpacing: '0.5px'
            }}>
              <DescriptionIcon sx={{
                verticalAlign: 'middle',
                mr: 1.5,
                fontSize: '32px'
              }} />
              {t("text.FileTransferHistory")} {/* Translated title */}
            </Typography>
            <Typography variant="subtitle1" sx={{
              opacity: 0.9,
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Numbers fontSize="small" />
              {transferData?.fileNumber || t("text.NA")}{" "}
              <strong>{t("text.FileName")}:</strong> {transferData?.fileName || t("text.NA")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>

        {/* Content area */}
        <Box sx={{
          p: 3,
          overflowY: 'auto',
          flex: 1,
          background: 'rgba(255,255,255,0.7)'
        }}>
          {loading ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px'
            }}>
              <CircularProgress size={60} thickness={4} sx={{ color: '#0077b6' }} />
            </Box>
          ) : historyData.length > 0 ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              position: 'relative',
              pl: 2,
              '&:before': {
                content: '""',
                position: 'absolute',
                left: '18px',
                top: 0,
                bottom: 0,
                width: '2px',
                backgroundColor: '#0077b6',
                opacity: 0.3
              }
            }}>
              {historyData.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    pl: 4,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(5px)'
                    }
                  }}
                >
                  {/* Timeline dot */}
                  <Box sx={{
                    position: 'absolute',
                    left: '8px',
                    top: '8px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#0077b6',
                    border: '4px solid #b2ebf2',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem'
                  }}>
                    {index + 1}
                  </Box>

                  {/* Timeline card */}
                  <Paper sx={{
                    p: 2.5,
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    background: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '6px',
                      background: 'linear-gradient(to bottom, #0077b6, #00b4d8)'
                    }
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      mb: 1.5
                    }}>
                      {/* Date */}
                      <Typography variant="caption" sx={{
                        color: 'text.secondary',
                        backgroundColor: '#e3f2fd',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontWeight: '500',
                        minWidth: '120px',
                        textAlign: 'center'
                      }}>
                        {moment.utc(item.createdDate).format("DD/MM/YYYY HH:mm")}
                      </Typography>

                      {/* Action type */}
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 600,
                        color: '#0077b6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <SwapHorizIcon fontSize="small" />
                        {item.actionType || t("text.Transfer")} {/* Translated default */}
                      </Typography>
                    </Box>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{
                          p: 2,
                          backgroundColor: '#e3f2fd',
                          borderRadius: '8px',
                          height: '100%'
                        }}>
                          <Typography variant="subtitle2" sx={{
                            fontWeight: 500,
                            color: '#0077b6',
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <LocationOnIcon fontSize="small" />
                            {t("text.Location")} {/* Translated label */}
                          </Typography>
                          <Typography>{item.fromLocation || t("text.NA")}</Typography>

                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{
                              fontWeight: 500,
                              color: '#0077b6',
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <PersonIcon fontSize="small" />
                              {t("text.Sender")} {/* Translated label */}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{
                                width: 32,
                                height: 32,
                                bgcolor: stringToColor(item.senderName || '?')
                              }}>
                                {item.senderName?.charAt(0) || '?'}
                              </Avatar>
                              <Typography>{item.senderName || t("text.NA")}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{
                          p: 2,
                          backgroundColor: '#e8f5e9',
                          borderRadius: '8px',
                          height: '100%'
                        }}>
                          <Typography variant="subtitle2" sx={{
                            fontWeight: 500,
                            color: '#0077b6',
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <LocationOnIcon fontSize="small" />
                            {t("text.Location")} {/* Translated label */}
                          </Typography>
                          <Typography>{item.toLocation || t("text.NA")}</Typography>

                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{
                              fontWeight: 500,
                              color: '#2e7d32',
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <PersonIcon fontSize="small" />
                              {t("text.Receiver")} {/* Translated label */}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{
                                width: 32,
                                height: 32,
                                bgcolor: stringToColor(item.receiverName || '?')
                              }}>
                                {item.receiverName?.charAt(0) || '?'}
                              </Avatar>
                              <Typography>{item.receiverName || t("text.NA")}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              textAlign: 'center'
            }}>
              <DescriptionIcon sx={{
                fontSize: '60px',
                color: '#b0bec5',
                mb: 2
              }} />
              <Typography variant="h6" sx={{ color: '#78909c' }}>
                {t("text.NoTransferHistoryFound")} {/* Translated message */}
              </Typography>
              <Typography variant="body2" sx={{ color: '#90a4ae' }}>
                {t("text.NoRecordedTransferHistory")} {/* Translated message */}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

// Helper component for timeline items
const TimelineItem = ({ icon, title, date }: { icon: React.ReactNode, title: string, date: string }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      flex: 1,
      minWidth: { sm: 0, md: '180px' }
    }}>
      <Box sx={{ color: 'primary.main' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {date}
        </Typography>
      </Box>
    </Box>
  );
};

// Helper component for consistent detail items
const DetailItem = ({
  label,
  value,
  icon
}: {
  label: string;
  value?: React.ReactNode;
  icon?: React.ReactNode
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{
      mb: 2,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5
    }}>
      {icon && (
        <Box sx={{
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: '2px'
        }}>
          {icon}
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {label}
        </Typography>
        {value ? (
          typeof value === 'string' || typeof value === 'number' ? (
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {value || t("text.NA")}
            </Typography>
          ) : (
            value
          )
        ) : (
          <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            {t("text.NA")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Helper function to generate color from string (for avatars)
const stringToColor = (string: string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
  }
  return color;
};

// Helper function to format dates
const formatDate = (dateString?: string | null) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

function Row({ row, index }: { row: any; index: number }) {
   const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileDocument, setFileDocument] = useState<FileDocument | null>(null);
  
   const fetchFileDocument = async (fileID: number) => {
      setFileLoading(true);
      try {
        const response = await api.post('FileDetail/GetFileDetails', {
          ZoneID: -1,
          wardID: -1,
          fileID: fileID,
          fileType: -1,
          fileGroup: -1,
          fileCategory: -1,
          fileClass: -1,
          fileSubject: -1
        });
        
        if (response.data.isSuccess && response.data.data && response.data.data.length > 0) {
          setFileDocument(response.data.data[0]);
          setFileModalOpen(true);
        }
      } catch (err) {
        console.error("Error fetching file document:", err);
      } finally {
        setFileLoading(false);
      }
    };
  
    const renderFileContent = () => {
      if (!fileDocument || !fileDocument.fileDoc) {
        return <Typography>{t("text.NoFileContentAvailable")}</Typography>;
      }
  
      const [mimeType, base64Data] = fileDocument.fileDoc.split(';base64,');
      
      if (mimeType.includes('application/pdf')) {
        return (
          <embed 
            src={fileDocument.fileDoc} 
            type="application/pdf" 
            width="100%" 
            height="100%"
            style={{ minHeight: '500px' }}
          />
        );
      } else if (mimeType.includes('image/')) {
        return (
          <img 
            src={fileDocument.fileDoc} 
            alt={fileDocument.fileName}
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        );
      } else {
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">{fileDocument.fileName}</Typography>
            <Typography>{t("text.UnsupportedFileFormat")}</Typography>
            <Button 
              variant="contained" 
              href={fileDocument.fileDoc} 
              download={fileDocument.fileName}
              sx={{ mt: 2 }}
            >
              {t("text.DownloadFile")}
            </Button>
          </Box>
        );
      }
    };
  
  
  return (
    <>
      <StyledTableRow>
        <StyledTableCell align="center">{index + 1}</StyledTableCell>
        <StyledTableCell align="center">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Typography
              onClick={() => setModalOpen(true)}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              {row.fileName || "NA"}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setHistoryModalOpen(true);
              }}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(103, 58, 183, 0.1)'
                }
              }}
            >
              <InfoIcon  fontSize="medium" />
            </IconButton>
          </Box>
        </StyledTableCell>
        <StyledTableCell align="center">{row.fromLocation || "NA"}</StyledTableCell>
        {/* <StyledTableCell align="center">{row.toLocation || "NA"}</StyledTableCell> */}
        <StyledTableCell align="center">{row.senderName || "NA"}</StyledTableCell>
        <StyledTableCell align="center">{row.receiverName || "NA"}</StyledTableCell>
        <StyledTableCell align="center">{moment.utc(row.createdDate).format("DD/MM/YYYY") || "NA"}</StyledTableCell>
       {/* Add the preview button cell */}
        <StyledTableCell align="center">
          <IconButton
            onClick={() => fetchFileDocument(row.fileID)}
            color="primary"
            size="small"
            disabled={fileLoading}
            sx={{ '&:hover': { backgroundColor: 'transparent' } }}
          >
            {fileLoading ? <CircularProgress size={24} /> : <VisibilityIcon />}
          </IconButton>
        </StyledTableCell>
      </StyledTableRow>

      <TransferHistoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        transferData={row}
      />



      <FileTransferHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        transferData={row}
      />

      {/* File Preview Modal */}
            <Modal
              open={fileModalOpen}
              onClose={() => setFileModalOpen(false)}
              aria-labelledby="file-preview-modal"
              aria-describedby="file-preview"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(2px)',
              }}
            >
              <Box sx={{
                width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '65vw' },
                height: { xs: '85vh', sm: '80vh', md: '75vh' },
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                border: '4px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'content-box, border-box',
                position: 'relative',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  zIndex: -1,
                  margin: '-4px',
                  borderRadius: 'inherit',
                  background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
                }
              }}>
                {/* Header with gradient background */}
                <Box sx={{
                  background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
                  color: 'white',
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("text.PreviewFile")} - {fileDocument?.fileName}
                  </Typography>
                  <IconButton 
                    onClick={() => setFileModalOpen(false)} 
                    sx={{ 
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                
                {/* File Content */}
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f5f5f5',
                  p: 2
                }}>
                  {renderFileContent()}
                </Box>
              </Box>
            </Modal>
    </>
  );
}

const PDFPreviewModal = ({ open, onClose, pdfBlob, onDownload }: PDFPreviewModalProps) => {
  const { t } = useTranslation();
  const [objectURL, setObjectURL] = useState<string | null>(null);

  useEffect(() => {
    if (pdfBlob && open) {
      const url = URL.createObjectURL(pdfBlob);
      setObjectURL(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [pdfBlob, open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="pdf-preview-modal"
      aria-describedby="pdf-preview"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
      }}
    >
      <Box sx={{
        width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '65vw' },
        height: { xs: '85vh', sm: '80vh', md: '75vh' },
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '4px solid transparent',
        backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'content-box, border-box',
        position: 'relative',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: -1,
          margin: '-4px',
          borderRadius: 'inherit',
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
        }
      }}>
        {/* Header with gradient background */}
        <Box sx={{
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          color: 'white',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("text.PDFPreview")}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* PDF Content */}
        <Box sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f5f5f5'
        }}>
          {objectURL ? (
            <iframe
              src={`${objectURL}#toolbar=0&navpanes=0`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
              title="PDF Preview"
            />
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2
            }}>
              <CircularProgress size={60} thickness={4} />
              <Typography variant="body1" color="textSecondary">
                {t("text.LoadingPDF")}...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer with actions */}
        <Box sx={{
          p: 2,
          bgcolor: 'grey.50',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              minWidth: 120,
              fontWeight: 600,
              borderRadius: 1,
              textTransform: 'none'
            }}
          >
            {t("text.Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={onDownload}
            sx={{
              minWidth: 120,
              fontWeight: 600,
              borderRadius: 1,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #303f9f 0%, #1976d2 100%)',
              }
            }}
          >
            {t("text.Download")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default function AuditTrailReport() {
  const { t } = useTranslation();

  const [fileCounts, setFileCounts] = useState({
    totalFile: 0,
    totEpcapprove: 0
  });
  const theme = useTheme();
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfDownloadFn, setPdfDownloadFn] = useState<(() => void) | null>(null);
  const [data, setData] = useState<any>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [optionLocation, setOptionLocation] = useState([
    { value: "-1", label: t("text.SelectFromLocation") },
  ]);
  const [optionMember, setOptionMember] = useState([
    { value: "-1", label: t("text.SelectSenderMember") },
  ]);
  const [optionFileDetailsAll, setOptionFileDetailsAll] = useState([
    { value: "-1", label: t("text.SelectFile") },
  ]);
  const [optionRefid, setOptionRefid] = useState([
    { label: t("text.SelectRFID") },
  ]);
  const [optionDepartment, setOptionDepartment] = useState([
    { value: "-1", label: t("text.SelectDepartment") },
  ]);



  // Date states
  const [FromDate, setFromDate] = useState<Date | null>(new Date('2025-01-01'));
  const [ToDate, setToDate] = useState<Date | null>(new Date());

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    getLocationData();
    getFileTypeData();
    getFileDtailsAllData();
    getDepartmentData();
  }, []);

  const getDepartmentData = () => {
    const collectData = {
      departmentId: -1,
    };

    api.post("Department/GetDepartmentmaster", collectData).then((res) => {
      const arr = [];
      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["departmentName"],
          value: res.data.data[index]["departmentId"],
        });
      }
      setOptionDepartment(arr);
    });
  };

  const getLocationData = () => {
    const collectData = {
      id: -1,
    };

    api.post("LocationItems/GetMappedLocation", collectData).then((res) => {
      const arr = [];
      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["location"],
          value: res.data.data[index]["id"],
        });
      }
      setOptionLocation(arr);
    });
  };

  const getFileTypeData = () => {
    const collectData = {
      useR_ID: "-1",
    };
    api.post(`USER/GetUSER`, collectData).then((res) => {
      const arr = res.data.data.map((item: any) => ({
        label: item.firsT_NAME + " " + item.middlE_NAME + "  " + item.suR_NAME,
        value: item.useR_ID,
      }));
      setOptionMember(arr);
    });
  };

  const getFileDtailsAllData = () => {
    const collectData = { "fileID": -1, "chackNo": "", "fileType": -1, "fileGroup": -1, "fileCategory": -1, "fileClass": -1, "fileSubject": -1, "zoneID": -1, "wardID": -1, "rfid": "", "user_ID": "" };

    api.post("FileDetail/GetFileDetails", collectData).then((res) => {
      const arr = [];
      const arr1: any = [];

      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["fileNo"] + " - " + res.data.data[index]["fileName"],
          value: res.data.data[index]["fileID"],
        });
      }

      for (let index = 0; index < res.data.data.length; index++) {
        const rfid = res.data.data[index]["rfid"];
        const refID = res.data.data[index]["fileID"];

        if (rfid !== "" && rfid !== 0) {
          arr1.push({
            label: rfid,
            value: refID,
          });
        }
      }
      setOptionRefid(arr1);
      setOptionFileDetailsAll(arr);
    });
  };

  interface FormValues {
    hid: any;
    fileID: any;
    fileNumber: string;
    fromLocation: string;
    toLocation: string;
    refId: string;
    sender: string;
    receiver: string;
    user_Id: string;

  }

  const initialValues: FormValues = {
    hid: -1,
    fileID: -1,
    fileNumber: "",
    fromLocation: "",
    toLocation: "",
    refId: "",
    sender: "",
    receiver: "",
    user_Id: "",

  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setHasSearched(true);
        setPage(0);

        // Base payload (always includes these fields)
        const payload: any = {
          hid: -1,
          fileID: values.fileID === -1 ? -1 : values.fileID,
          fileNumber: values.fileNumber || "",
          fromLocation: values.fromLocation || "",
          toLocation: values.toLocation || "",
          refId: values.refId || "",
          sender: values.sender || "",
          receiver: values.receiver || "",
          user_Id: values.user_Id || "",
        };

        // Only add dates if NOT filtering by fileID
        // Only add dates if NOT filtering by fileID or user_Id
        if (values.fileID === -1 && (!values.user_Id || values.user_Id === "-1" || values.user_Id === "")) {
          payload.FromDate = FromDate
            ? moment(FromDate).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
            : moment(new Date('2025-01-01')).utc().format("YYYY-MM-DDTHH:mm:ss[Z]");

          payload.ToDate = ToDate
            ? moment(ToDate).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
            : moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
        }

        const response = await api.post(
          `FileMovementHistory/GetAuditTrailReport`,
          payload
        );

        setData(response.data.data);
        setFileCounts({
          totalFile: response.data.totalFile || 0,
          totEpcapprove: response.data.totEpcapprove || 0
        });

      } catch (error) {
        toast.error("An error occurred while fetching data");
      } finally {
        setLoading(false);
      }
    },
  });

  // const handlePrint = () => {
  //   const contentElement = document.getElementById("tabcont");
  //   if (contentElement) {
  //     const clonedContent = contentElement.cloneNode(true) as HTMLElement;

  //     const filterButton = clonedContent.querySelector(".filter-button");
  //     if (filterButton) {
  //       filterButton.remove();
  //     }
  //     const printButton = clonedContent.querySelector(".print-button");
  //     if (printButton) {
  //       printButton.remove();
  //     }

  //     const printWindow = window.open("", "_blank");
  //     if (printWindow) {
  //       printWindow.document.body.appendChild(clonedContent);
  //       printWindow.print();
  //     } else {
  //       console.error("Error: Unable to open print window.");
  //     }
  //   }
  // };

  const exportToPDF = () => {
    if (data.length === 0) {
      toast.warning(t("text.NoDataToExport"));
      return;
    }

    // Create a new PDF document in landscape
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4"
    });

    // Add title and metadata
    doc.setProperties({
      title: "Audit Trail Report",
      subject: "File Transfer History",
      author: "PanchSala",
      creator: "PanchSala Document Management System"
    });

    // Add header with title
    doc.setFontSize(20);
    doc.setTextColor(63, 81, 181); // Dark blue
    doc.setFont("helvetica", "bold");
    doc.text("AUDIT TRAIL REPORT", doc.internal.pageSize.width / 2, 20, {
      align: "center",
      renderingMode: "fill"
    });

    // Add subtitle with date range
    doc.setFontSize(12);
    doc.setTextColor(81, 81, 81); // Dark gray
    doc.setFont("helvetica", "normal");
    doc.text(
      `Period: ${moment.utc(FromDate).format("DD/MM/YYYY")} - ${moment.utc(ToDate).format("DD/MM/YYYY")}`,
      doc.internal.pageSize.width / 2,
      27,
      { align: "center" }
    );

    // Add generated timestamp
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120); // Light gray
    doc.text(
      `Generated on: ${moment().format("DD/MM/YYYY HH:mm")}`,
      doc.internal.pageSize.width / 2,
      33,
      { align: "center" }
    );

    // Add decorative line
    doc.setDrawColor(63, 81, 181); // Dark blue
    doc.setLineWidth(0.5);
    doc.line(15, 36, doc.internal.pageSize.width - 15, 36);

    // Add summary section with better styling
    doc.setFontSize(12);
    doc.setTextColor(63, 81, 181); // Dark blue
    doc.setFont("helvetica", "bold");
    doc.text("Summary Statistics", 15, 43);

    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0); // Black
    doc.setFont("helvetica", "normal");
    doc.text(`• Total Files: ${fileCounts.totalFile}`, 15, 49);
    doc.text(`• Approved Files: ${fileCounts.totEpcapprove}`, 15, 55);
    // doc.text(`• Total Transfers: ${data.length}`, 15, 61);

    // Add a horizontal line separator with gradient effect
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(15, 64, doc.internal.pageSize.width - 15, 64);

    // Prepare data for the table with better column widths
    const headers = [
      { content: "Sr No", styles: { halign: 'center', cellWidth: 15, fontStyle: 'bold' } },
      { content: "File Name", styles: { halign: 'center', cellWidth: 50, fontStyle: 'bold' } },
      { content: "Location", styles: { halign: 'center', cellWidth: 60, fontStyle: 'bold' } },
      { content: "Sender", styles: { halign: 'center', cellWidth: 50, fontStyle: 'bold' } },
      { content: "Receiver", styles: { halign: 'center', cellWidth: 50, fontStyle: 'bold' } },
      { content: "Transfer Date", styles: { halign: 'center', cellWidth: 45, fontStyle: 'bold' } },
     
    ];

    const tableData = data.map((row: any, index: number) => [
      { content: index + 1, styles: { halign: 'center' } },
      { content: row.fileName || "N/A", styles: { fontStyle: 'bold' } },
      { content: row.fromLocation?.replace(/-/g, ' ') || "N/A" },
      { content: row.senderName || "N/A" },
      { content: row.receiverName || "N/A" },
      { content: moment.utc(row.createdDate).format("DD/MM/YYYY") || "N/A", styles: { halign: 'center' } },
     
    ]);

    // Add the table using autoTable with premium styling
    autoTable(doc, {
      startY: 68,
      head: [headers.map(h => h.content)],
      body: tableData,
      theme: "grid",
      headStyles: {
        fillColor: [63, 81, 181], // Dark blue
        textColor: 255, // White
        fontStyle: "bold",
        halign: 'center',
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240], // Very light gray
      },
      styles: {
        cellPadding: 3,
        fontSize: 10,
        valign: 'middle',
        overflow: 'linebreak',
        minCellHeight: 8,
        lineColor: [220, 220, 220], // Light gray border
        lineWidth: 0.3,
        font: "helvetica"
      },
      columnStyles: headers.reduce((acc: any, header, index) => {
        acc[index] = header.styles;
        return acc;
      }, {}),
      margin: { left: 10, right: 10 },
      tableWidth: 'auto',
      didDrawPage: function (data) {
        // Footer on each page
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);

        // Left footer
        // doc.text(
        //   `© ${new Date().getFullYear()} PanchSala. All rights reserved.`,
        //   15,
        //   doc.internal.pageSize.height - 10
        // );

        // Center footer
        // doc.text(
        //   "CONFIDENTIAL",
        //   doc.internal.pageSize.width / 2,
        //   doc.internal.pageSize.height - 10,
        //   { align: "center" }
        // );

        // // Right footer
        // doc.text(
        //   `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
        //   doc.internal.pageSize.width - 15,
        //   doc.internal.pageSize.height - 10,
        //   { align: "right" }
        // );
      }
    });

    // Generate PDF blob
    const pdfBlob = doc.output('blob');
    setPdfBlob(pdfBlob);

    // Set up download function
    const downloadFn = () => {
      doc.save(`Audit_Trail_Report_${moment().format("YYYY-MM-DD_HHmm")}.pdf`);
      setPdfModalOpen(false);
    };
    setPdfDownloadFn(() => downloadFn);

    // Open preview modal
    setPdfModalOpen(true);
  };

  const exportToExcel = () => {
    if (data.length === 0) {
      toast.warning(t("text.NoDataToExport"));
      return;
    }

    // Prepare the worksheet data
    const excelData = data.map((row: any, index: number) => ({
      "Sr No": index + 1,
      "File Name": row.fileName || "N/A",
      "Location": row.fromLocation?.replace(/-/g, ' ') || "N/A", // Clean up location format
      // "To Location": row.toLocation?.replace(/-/g, ' ') || "N/A",
      // "RFID": row.refId || "N/A",
      "Sender": row.senderName || "N/A",
      "Receiver": row.receiverName || "N/A",
      "Transfer Date": moment.utc(row.createdDate).format("DD/MM/YYYY") || "N/A",
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Calculate maximum width for each column
    const colWidths = [
      { wch: 8 },  // Sr No (fixed width)
    ];

    // Calculate dynamic widths for other columns
    const headers = ["File Name", "Location", "Sender", "Receiver", "Transfer Date"];
    headers.forEach((header, colIndex) => {
      // Start with header width
      let maxWidth = header.length * 1.3; // Character width factor

      // Check all data rows for this column
      excelData.forEach((row: { [x: string]: { toString: () => string; }; }) => {
        const value = row[header]?.toString() || "";
        const cellWidth = value.length * 1.1; // Slightly less than header factor
        if (cellWidth > maxWidth) {
          maxWidth = cellWidth;
        }
      });

      // Apply minimum and maximum limits
      maxWidth = Math.max(10, Math.min(maxWidth, 50)); // Min 10, Max 50
      colWidths.push({ wch: maxWidth });
    });

    // Set column widths
    ws["!cols"] = colWidths;

    // Style definitions
    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
        sz: 12,
        name: "Calibri"
      },
      fill: {
        fgColor: { rgb: "203864" } // Navy blue
      },
      border: {
        top: { style: "medium", color: { rgb: "1F497D" } },
        bottom: { style: "medium", color: { rgb: "1F497D" } },
        left: { style: "medium", color: { rgb: "1F497D" } },
        right: { style: "medium", color: { rgb: "1F497D" } }
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true
      }
    };

    const cellStyle = {
      font: {
        sz: 11,
        name: "Calibri"
      },
      border: {
        top: { style: "thin", color: { rgb: "D9D9D9" } },
        bottom: { style: "thin", color: { rgb: "D9D9D9" } },
        left: { style: "thin", color: { rgb: "D9D9D9" } },
        right: { style: "thin", color: { rgb: "D9D9D9" } }
      },
      alignment: {
        vertical: "center"
      }
    };

    // Apply styles to all cells
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1:H" + (excelData.length + 1));
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) ws[cellAddress] = {};

        if (R === 0) {
          // Header row
          ws[cellAddress].s = headerStyle;
        } else {
          // Data rows
          ws[cellAddress].s = {
            ...cellStyle,
            alignment: {
              ...cellStyle.alignment,
              horizontal: C === 0 || C === 7 ? "center" : "left" // Center align Sr No and Date columns
            }
          };

          // Alternate row coloring
          if (R % 2 === 0) {
            ws[cellAddress].s.fill = {
              fgColor: { rgb: "F2F2F2" } // Light gray for even rows
            };
          }
        }
      }
    }

    // Freeze header row
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomRight" };

    // Add filters to header row
    ws["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Audit Trail");

    // Add metadata
    wb.Props = {
      Title: "Audit Trail Report",
      Subject: "File Transfer History",
      Author: "Your Application",
      CreatedDate: new Date(),
      Company: "Your Company Name"
    };

    // Add a custom header/footer
    wb.Workbook = {
      Views: [{ RTL: false }]
    };

    // Generate Excel file
    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      bookSST: true,
      cellStyles: true
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });

    saveAs(blob, `Audit_Trail_Report_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleReset = () => {
    formik.resetForm({
      values: initialValues,
    });
    setFromDate(new Date('2025-01-01'));
    setToDate(new Date());
    setData([]);
    setHasSearched(false);
    setFileCounts({
      totalFile: 0,
      totEpcapprove: 0,
    });
    setPage(0);
  };

  return (
    <Card
      sx={{
        width: "100%",
        boxShadow: theme.shadows[4],
        borderRadius: "12px",
        overflow: "hidden",
        marginTop: "24px",
      }}
    >
      <ToastContainer></ToastContainer>
      <Box
        sx={{
          p: 3,
          backgroundColor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <DescriptionIcon
                sx={{
                  color: "primary.main",
                  fontSize: 32,
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                {t("text.AuditTrailReport")}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<FilterAltIcon />}
                  onClick={toggleFilters}
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "6px",
                    px: 3,
                    py: 1,
                  }}
                >
                  {t("text.Filter")}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={exportToPDF}
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "6px",
                    px: 3,
                    py: 1,
                    borderWidth: "2px",
                    "&:hover": {
                      borderWidth: "2px",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  {t("text.PDF")}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={exportToExcel}
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "6px",
                    px: 3,
                    py: 1,
                    borderWidth: "2px",
                    "&:hover": {
                      borderWidth: "2px",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  {t("text.Excel")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {showFilters && (
        <Box
          sx={{
            p: 3,
            backgroundColor: "background.default",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* Always visible basic filters */}


              <Grid item xs={12} sm={6} md={4} lg={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("text.FromDate")}
                    value={FromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: { fullWidth: true, size: "small" }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("text.ToDate")}
                    value={ToDate}
                    onChange={(newValue) => setToDate(newValue)}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: { fullWidth: true, size: "small" }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="user-select"
                  options={optionMember}
                  fullWidth
                  size="small"
                  value={
                    optionMember.find(
                      (option) => option.value === formik.values.user_Id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("user_Id", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectUser")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="file-select"
                  options={optionFileDetailsAll}
                  fullWidth
                  size="small"
                  value={
                    optionFileDetailsAll.find(
                      (option) => option.value === formik.values.fileID
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("fileID", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectFile")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Grid container spacing={2}>

                  <Grid item xs={6}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{
                        height: "40px",
                        borderRadius: "6px",
                        fontWeight: 600,
                      }}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        t("text.search")
                      )}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      sx={{
                        height: "40px",
                        borderRadius: "6px",
                        fontWeight: 600,
                      }}
                      onClick={handleReset}
                      disabled={loading}
                    >
                      {t("text.Clear")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>


            </Grid>
          </form>
        </Box>
      )}
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "background.default",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", gap: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("text.CreatedFiles")}:{" "}
            <span style={{ color: "#673ab7" }}>
              {fileCounts.totalFile}
            </span>
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("text.AssignedFiles")}:{" "}
            <span style={{ color: "#9c27b0" }}>
              {fileCounts.totEpcapprove}
            </span>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <TableContainer
          component={Paper}
          id="tabcont"
          sx={{
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
            overflow: "auto",
            maxHeight: "calc(100vh - 300px)",
          }}
        >
          <Table
            aria-label="Audit Trail table"
            stickyHeader
            sx={{
              minWidth: 650,
              "& .MuiTableCell-root": {
                padding: "12px 16px",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">
                  {t("text.SrNo")}
                </StyledTableCell>
                {/* <StyledTableCell align="center">
                  {t("text.FileNo")}
                </StyledTableCell> */}
                <StyledTableCell align="center">
                  {t("text.FileName")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("text.Location")}
                </StyledTableCell>
                {/* <StyledTableCell align="center">
                  {t("text.ToLocation")}
                </StyledTableCell> */}
                {/* <StyledTableCell align="center">
                  {t("text.RFID")}
                </StyledTableCell> */}
                <StyledTableCell align="center">
                  {t("text.Sender")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("text.Receiver")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("text.TransferDate")}
                </StyledTableCell>
                   <StyledTableCell align="center">{t("text.PreviewFile")}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data && data.length > 0 ? (
                data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any, index: number) => (
                    <Row
                      key={`${row.hid}-${index}`}
                      row={row}
                      index={page * rowsPerPage + index}
                    />
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {hasSearched
                        ? t("text.Nodatafoundfortheselectedfilters")
                        : t("text.Pleaseapplyfiltersandclicksearchtoviewdata")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderTop: 0,
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}
        >
          <Box sx={{ flex: 1 }} />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
            colSpan={3}
            count={data ? data.length : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: {
                "aria-label": "rows per page",
              },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
            sx={{
              border: "none",
              "& .MuiTablePagination-toolbar": {
                padding: 0,
              },
              "& .MuiTablePagination-selectLabel": {
                margin: 0,
              },
              "& .MuiTablePagination-displayedRows": {
                margin: 0,
              },
            }}
          />
        </Paper>
      </Box>

      <iframe
        id="ifmcontentstoprint"
        style={{ height: "0px", width: "0px", position: "absolute" }}
      />

      <PDFPreviewModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfBlob={pdfBlob}
        onDownload={pdfDownloadFn || (() => { })}
      />
    </Card>
  );
}